import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import CompareArrowsOutlinedIcon from '@mui/icons-material/CompareArrowsOutlined';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    Tooltip,
    Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';

import { fetchIdioVolComparison } from '../utils/ApiCall';
import {
    formatUserFactorModel,
    normalizeIdioVolSource,
    normalizeUserFactorModel,
} from '../utils/factorDataOptions';
import { getUploadedIdentifierForRow } from '../utils/identifierDisplay';
import { compactStripedTableSx } from './pos_sizing_table/tableStyles';
import {
    secondaryActionButtonSx,
    secondaryModalSx,
} from '../styles/secondaryMsiStyles';
import {
    APP_STYLE_COMPONENT_COLORS as MSI_COMPONENT_COLORS,
    MSI_GEOMETRY,
    APP_STYLE_PALETTE as MSI_PALETTE,
    usesAppStyleGrammar,
} from '../styles/msiStyleTokens';

const formatContextLabel = (value) => (
    value ? String(value).trim() : '—'
);

const normalizeSedolKey = (value) => (
    value == null ? '' : String(value).trim()
);

const getUploadedIdentifierLabel = (row) => {
    const { type, value } = getUploadedIdentifierForRow(row);
    return value ? [type, value].filter(Boolean).join(' ') : '';
};

const formatRiskValue = (value, digits = 2) => {
    if (value === null || value === undefined || value === '') {
        return '—';
    }
    const numeric = Number(value);
    if (!Number.isFinite(numeric)) {
        return '—';
    }
    return `${(numeric * 100).toFixed(digits)}%`;
};

const formatDifferenceValue = (value) => {
    if (value === null || value === undefined || value === '') {
        return '—';
    }
    const numeric = Number(value);
    if (!Number.isFinite(numeric)) {
        return '—';
    }
    const sign = numeric > 0 ? '+' : '';
    return `${sign}${(numeric * 100).toFixed(2)}%`;
};

const formatMetricValue = (value, format = 'number') => {
    if (value === null || value === undefined || value === '') {
        return '—';
    }
    const numeric = Number(value);
    if (!Number.isFinite(numeric)) {
        return '—';
    }
    if (format === 'pct') {
        return `${numeric.toFixed(2)}%`;
    }
    if (format === 'bps') {
        return `${numeric.toFixed(2)} bps`;
    }
    return numeric.toFixed(2);
};

const formatMetricDeltaValue = (value, format = 'number') => {
    if (value === null || value === undefined || value === '') {
        return '—';
    }
    const numeric = Number(value);
    if (!Number.isFinite(numeric)) {
        return '—';
    }
    const sign = numeric > 0 ? '+' : '';
    if (format === 'pct') {
        return `${sign}${numeric.toFixed(2)}%`;
    }
    if (format === 'bps') {
        return `${sign}${numeric.toFixed(2)} bps`;
    }
    return `${sign}${numeric.toFixed(2)}`;
};

const getDeltaCellSx = (value) => ({
    color: (theme) => {
        const numeric = Number(value);
        if (!Number.isFinite(numeric) || numeric === 0) {
            return theme.palette.text.primary;
        }
        return numeric > 0 ? theme.palette.success.main : theme.palette.error.main;
    },
});

const formatWeightPct = (value) => {
    if (value === null || value === undefined || value === '') {
        return '—';
    }
    const numeric = Number(value);
    if (!Number.isFinite(numeric)) {
        return '—';
    }
    return `${numeric.toFixed(1)}%`;
};

const formatCountValue = (value) => {
    if (value === null || value === undefined || value === '') {
        return '—';
    }
    const numeric = Number(value);
    if (!Number.isFinite(numeric)) {
        return '—';
    }
    return numeric.toLocaleString();
};

const formatCoverageValue = (count, grossWeightPct = null) => {
    const countText = formatCountValue(count);
    if (countText === '—') {
        return '—';
    }
    const weightText = formatWeightPct(grossWeightPct);
    if (weightText === '—') {
        return countText;
    }
    return `${countText} (${weightText})`;
};

const renderBooleanCheck = (checked) => (
    checked ? <CheckRoundedIcon sx={{ fontSize: 15, color: 'success.main', verticalAlign: 'middle' }} /> : null
);

const compareSharedBorderColor = (theme) => alpha(
    usesAppStyleGrammar(theme) ? MSI_PALETTE.line : theme.palette.divider,
    usesAppStyleGrammar(theme) ? 1 : (theme.palette.mode === 'dark' ? 0.34 : 0.62),
);

const comparePanelSurfaceColor = (theme) => (
    usesAppStyleGrammar(theme)
        ? MSI_PALETTE.surface
        : theme.palette.mode === 'dark'
        ? alpha(theme.palette.background.paper, 0.56)
        : alpha('#ffffff', 0.88)
);

const compareMutedSurfaceColor = (theme) => (
    usesAppStyleGrammar(theme)
        ? MSI_PALETTE.surfaceSubtle
        : theme.palette.mode === 'dark'
        ? alpha(theme.palette.background.paper, 0.38)
        : alpha('#ffffff', 0.72)
);

const compareSectionPaperSx = {
    borderRadius: (theme) => (
        usesAppStyleGrammar(theme) ? `${MSI_GEOMETRY.radii.panel}px` : 3
    ),
    border: '1px solid',
    borderColor: (theme) => compareSharedBorderColor(theme),
    backgroundColor: (theme) => comparePanelSurfaceColor(theme),
    backgroundImage: (theme) => (usesAppStyleGrammar(theme) ? 'none' : undefined),
    boxShadow: 'none',
    overflow: 'hidden',
};

const compareInsetPaperSx = {
    borderRadius: (theme) => (
        usesAppStyleGrammar(theme) ? `${MSI_GEOMETRY.radii.button}px` : 2.5
    ),
    border: '1px solid',
    borderColor: (theme) => compareSharedBorderColor(theme),
    backgroundColor: (theme) => compareMutedSurfaceColor(theme),
    backgroundImage: (theme) => (usesAppStyleGrammar(theme) ? 'none' : undefined),
    boxShadow: 'none',
};

const compareTableContainerSx = {
    ...compareInsetPaperSx,
    '& .MuiTableCell-root': {
        borderBottomColor: (theme) => compareSharedBorderColor(theme),
    },
};

const compareStripedTableSx = {
    ...compactStripedTableSx,
    '& th': {
        ...compactStripedTableSx['& th'],
        fontWeight: 700,
        backgroundColor: (theme) => (
            usesAppStyleGrammar(theme) ? MSI_COMPONENT_COLORS.tableHeader : theme.palette.background.paper
        ),
        backgroundImage: 'none',
        backgroundClip: 'padding-box',
    },
    '& .MuiTableCell-stickyHeader': {
        backgroundColor: (theme) => `${
            usesAppStyleGrammar(theme) ? MSI_COMPONENT_COLORS.tableHeader : theme.palette.background.paper
        } !important`,
        backgroundImage: 'none',
        backgroundClip: 'padding-box',
    },
    '& thead th': {
        backgroundColor: (theme) => `${
            usesAppStyleGrammar(theme) ? MSI_COMPONENT_COLORS.tableHeader : theme.palette.background.paper
        } !important`,
        backgroundImage: 'none',
        backgroundClip: 'padding-box',
    },
    '& tbody tr:nth-of-type(even) td': {
        backgroundColor: (theme) => (
            usesAppStyleGrammar(theme) ? MSI_PALETTE.surface : alpha(theme.palette.action.hover, 0.11)
        ),
    },
    '& tbody tr:hover td': {
        backgroundColor: (theme) => alpha(theme.palette.action.hover, 0.18),
    },
};

const securityComparisonColumns = [
    { id: 'ticker', label: 'Ticker', type: 'string', align: 'left' },
    { id: 'identifier', label: 'Identifier', type: 'string', align: 'left' },
    { id: 'standard_idio_vol', label: 'Standard', type: 'number', align: 'right' },
    { id: 'ai_idio_vol', label: 'AI', type: 'number', align: 'right' },
    { id: 'difference', label: 'Difference', type: 'number', align: 'right' },
    { id: 'gross_weight_pct', label: 'Gross wt', type: 'number', align: 'right' },
    { id: 'standard_specific_contribution_bps', label: 'Standard contrib', type: 'number', align: 'right' },
    { id: 'ai_specific_contribution_bps', label: 'AI contrib', type: 'number', align: 'right' },
    { id: 'delta_specific_contribution_bps', label: 'Delta contrib', type: 'number', align: 'right' },
    { id: 'used_ai_prediction', label: 'AI used', type: 'boolean', align: 'center' },
    { id: 'inPortfolio', label: 'In portfolio', type: 'boolean', align: 'center' },
];

const getTickerTieBreak = (row) => {
    const ticker = String(row?.ticker || '').trim().toLowerCase();
    const sedol = String(row?.sedol || '').trim().toLowerCase();
    return `${ticker}|${sedol}`;
};

const compareSecurityValues = (left, right, column, direction) => {
    const leftValue = left?.[column.id];
    const rightValue = right?.[column.id];
    const leftMissing = leftValue === null || leftValue === undefined || leftValue === '';
    const rightMissing = rightValue === null || rightValue === undefined || rightValue === '';

    if (column.type === 'number') {
        if (leftMissing && rightMissing) {
            return 0;
        }
        if (leftMissing) {
            return 1;
        }
        if (rightMissing) {
            return -1;
        }
        const leftNumber = Number(leftValue);
        const rightNumber = Number(rightValue);
        if (leftNumber === rightNumber) {
            return 0;
        }
        return direction === 'asc'
            ? leftNumber - rightNumber
            : rightNumber - leftNumber;
    }

    if (column.type === 'boolean') {
        const leftBoolean = Boolean(leftValue);
        const rightBoolean = Boolean(rightValue);
        if (leftBoolean === rightBoolean) {
            return 0;
        }
        const leftNumber = Number(leftBoolean);
        const rightNumber = Number(rightBoolean);
        return direction === 'asc'
            ? leftNumber - rightNumber
            : rightNumber - leftNumber;
    }

    const leftText = String(leftValue || '').trim().toLowerCase();
    const rightText = String(rightValue || '').trim().toLowerCase();
    if (leftText === rightText) {
        return 0;
    }
    return direction === 'asc'
        ? leftText.localeCompare(rightText)
        : rightText.localeCompare(leftText);
};

const sortCompareRows = (rows) => (
    [...rows].sort((left, right) => {
        const leftKey = String(left?.ticker || left?.sedol || '').toLowerCase();
        const rightKey = String(right?.ticker || right?.sedol || '').toLowerCase();
        if (leftKey !== rightKey) {
            return leftKey.localeCompare(rightKey);
        }
        return String(left?.sedol || '').localeCompare(String(right?.sedol || ''));
    })
);

export default function IdioVolCompareTool({
    factorModel,
    factorDataModel,
    factorSnapshotDate,
    idioVolSource,
}) {
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [payload, setPayload] = useState(null);
    const [securitySort, setSecuritySort] = useState({ field: 'ticker', direction: 'asc' });

    useEffect(() => {
        let cancelled = false;

        if (!open) {
            return () => {
                cancelled = true;
            };
        }

        setLoading(true);
        setError('');

        fetchIdioVolComparison({
            factorModel,
            factorDataModel,
            factorSnapshotDate,
            idioVolSource,
        })
            .then((response) => {
                if (cancelled) {
                    return;
                }
                if (response?.success === false || !response || !Array.isArray(response.rows)) {
                    throw new Error(response?.message || 'Unable to load compare data.');
                }
                setPayload(response);
            })
            .catch((loadError) => {
                if (cancelled) {
                    return;
                }
                setPayload(null);
                setError(loadError?.message || 'Unable to load compare data.');
            })
            .finally(() => {
                if (!cancelled) {
                    setLoading(false);
                }
            });

        return () => {
            cancelled = true;
        };
    }, [factorDataModel, factorModel, factorSnapshotDate, idioVolSource, open]);

    const rows = useMemo(
        () => sortCompareRows(payload?.rows || []),
        [payload?.rows],
    );
    const portfolioCompare = payload?.portfolio_compare || null;
    const summary = payload?.summary || {};
    const context = payload?.context || {};
    const currentSourceLabel = normalizeIdioVolSource(context?.current_idio_vol_source || idioVolSource) === 'ai'
        ? 'AI'
        : 'Standard';
    const effectiveFactorModel = normalizeUserFactorModel(context?.factor_model || factorModel);
    const portfolioCoverageByLabel = useMemo(() => {
        const map = new Map();
        (portfolioCompare?.coverage_rows || []).forEach((row) => {
            if (row?.label) {
                map.set(row.label, row);
            }
        });
        return map;
    }, [portfolioCompare?.coverage_rows]);
    const portfolioSedolSet = useMemo(
        () => new Set((portfolioCompare?.portfolio_sedols || []).map(normalizeSedolKey).filter(Boolean)),
        [portfolioCompare?.portfolio_sedols],
    );
    const portfolioSecurityBySedol = useMemo(() => {
        const map = new Map();
        (portfolioCompare?.security_rows || []).forEach((row) => {
            const sedolKey = normalizeSedolKey(row?.sedol);
            if (sedolKey) {
                map.set(sedolKey, row);
            }
        });
        return map;
    }, [portfolioCompare?.security_rows]);
    const coverageRows = useMemo(() => {
        const aiUsedRow = portfolioCoverageByLabel.get('AI predictions used');
        const fallbackRow = portfolioCoverageByLabel.get('Standard fallback used');
        const portfolioUnavailableText = portfolioCompare?.message || 'Not available';
        const watchlistEvaluatedCount = Number(summary?.compared_security_count);
        const portfolioEvaluatedCount = Number(portfolioCompare?.compared_position_count);
        const formatCoveragePct = (numerator, denominator) => {
            const numeratorValue = Number(numerator);
            const denominatorValue = Number(denominator);
            if (!Number.isFinite(numeratorValue) || !Number.isFinite(denominatorValue) || denominatorValue <= 0) {
                return null;
            }
            return (numeratorValue / denominatorValue) * 100;
        };

        return [
            {
                metric: 'Universe size',
                watchlistValue: formatCountValue(summary.watchlist_security_count),
                portfolioValue: portfolioCompare?.available
                    ? formatCountValue(portfolioCompare?.portfolio_position_count)
                    : portfolioUnavailableText,
            },
            {
                metric: 'AI used',
                watchlistValue: formatCoverageValue(
                    summary.ai_prediction_used_count,
                    formatCoveragePct(summary.ai_prediction_used_count, watchlistEvaluatedCount),
                ),
                portfolioValue: portfolioCompare?.available
                    ? formatCoverageValue(
                        aiUsedRow?.count,
                        formatCoveragePct(aiUsedRow?.count, portfolioEvaluatedCount),
                    )
                    : portfolioUnavailableText,
            },
            {
                metric: 'Standard fallback used',
                watchlistValue: formatCoverageValue(
                    summary.standard_fallback_used_count,
                    formatCoveragePct(summary.standard_fallback_used_count, watchlistEvaluatedCount),
                ),
                portfolioValue: portfolioCompare?.available
                    ? formatCoverageValue(
                        fallbackRow?.count,
                        formatCoveragePct(fallbackRow?.count, portfolioEvaluatedCount),
                    )
                    : portfolioUnavailableText,
            },
        ];
    }, [portfolioCompare, portfolioCoverageByLabel, summary]);
    const mergedRows = useMemo(
        () => rows.map((row) => {
            const sedolKey = normalizeSedolKey(row.sedol);
            const portfolioRow = portfolioSecurityBySedol.get(sedolKey);
            return {
                ...row,
                identifier: getUploadedIdentifierLabel(row),
                inPortfolio: portfolioSedolSet.has(sedolKey),
                gross_weight_pct: portfolioRow?.gross_weight_pct ?? null,
                standard_specific_contribution_bps: portfolioRow?.standard_specific_contribution_bps ?? null,
                ai_specific_contribution_bps: portfolioRow?.ai_specific_contribution_bps ?? null,
                delta_specific_contribution_bps: portfolioRow?.delta_specific_contribution_bps ?? null,
            };
        }),
        [portfolioSecurityBySedol, portfolioSedolSet, rows],
    );
    const sortedSecurityRows = useMemo(() => {
        const activeColumn = securityComparisonColumns.find((column) => column.id === securitySort.field)
            || securityComparisonColumns[0];
        return [...mergedRows].sort((left, right) => {
            const valueComparison = compareSecurityValues(left, right, activeColumn, securitySort.direction);
            if (valueComparison !== 0) {
                return valueComparison;
            }
            return getTickerTieBreak(left).localeCompare(getTickerTieBreak(right));
        });
    }, [mergedRows, securitySort]);
    const handleSecuritySort = (field) => {
        const selectedColumn = securityComparisonColumns.find((column) => column.id === field);
        if (!selectedColumn) {
            return;
        }
        setSecuritySort((current) => {
            if (current.field === field) {
                return {
                    field,
                    direction: current.direction === 'asc' ? 'desc' : 'asc',
                };
            }
            return {
                field,
                direction: selectedColumn.type === 'boolean' ? 'desc' : 'asc',
            };
        });
    };

    return (
        <>
            <Box sx={{ pt: 1.15, display: 'grid', gap: 0.9 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                    <Box sx={{ display: 'grid', gap: 0.35 }}>
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                            AI vs Standard Compare
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            Compare actual-portfolio risk impact and detailed security-level idio vols without changing the main Risk Overview tables.
                        </Typography>
                    </Box>
                    <Button
                        size="small"
                        variant="outlined"
                        startIcon={<CompareArrowsOutlinedIcon sx={{ fontSize: 16 }} />}
                        onClick={() => setOpen(true)}
                        sx={{ textTransform: 'none', fontWeight: 700, ...secondaryActionButtonSx }}
                    >
                        Compare
                    </Button>
                </Box>
            </Box>

            <Dialog
                open={open}
                onClose={() => setOpen(false)}
                fullWidth
                maxWidth="xl"
                PaperProps={{ sx: secondaryModalSx }}
            >
                <DialogTitle sx={{ px: 2, py: 1.5 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                        AI vs Standard Compare
                    </Typography>
                </DialogTitle>
                <DialogContent dividers sx={{ px: 2, py: 1.5 }}>
                    <Box sx={{ display: 'grid', gap: 1.25 }}>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.8 }}>
                            <Chip
                                label={`Provider ${formatUserFactorModel(effectiveFactorModel)}`}
                                variant="outlined"
                                sx={{ fontWeight: 700 }}
                            />
                            <Chip
                                label={`Factor-data model ${formatContextLabel(context?.factor_data_model || factorDataModel)}`}
                                variant="outlined"
                                sx={{ fontWeight: 700, fontFamily: 'monospace' }}
                            />
                            <Chip
                                label={`Snapshot ${formatContextLabel(context?.factor_snapshot_date || factorSnapshotDate)}`}
                                variant="outlined"
                                sx={{ fontWeight: 700 }}
                            />
                            <Chip
                                label={`Current source ${currentSourceLabel}`}
                                color={currentSourceLabel === 'AI' ? 'primary' : 'default'}
                                variant="outlined"
                                sx={{ fontWeight: 700 }}
                            />
                        </Box>

                        {loading && (
                            <Paper sx={{ ...compareInsetPaperSx, p: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                                <CircularProgress size={18} />
                                <Typography variant="body2" color="text.secondary">
                                    Loading compare data...
                                </Typography>
                            </Paper>
                        )}

                        {!loading && error && (
                            <Alert severity="error">
                                {error}
                            </Alert>
                        )}

                        {!loading && !error && (
                            <Paper sx={{ ...compareSectionPaperSx, p: 1.5, display: 'grid', gap: 1.25 }}>
                                <Box sx={{ display: 'grid', gap: 0.4 }}>
                                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                        Coverage
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        Consolidated coverage for the watchlist compare universe and the current actual portfolio. Percentages for AI usage and fallback are based on the names evaluated in this run for each scope.
                                    </Typography>
                                </Box>
                                <TableContainer component={Paper} sx={compareTableContainerSx}>
                                    <Table size="small" aria-label="AI vs standard coverage table" sx={compareStripedTableSx}>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Metric</TableCell>
                                                <TableCell align="right">Watchlist</TableCell>
                                                <TableCell align="right">Actual portfolio</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {coverageRows.map((row) => (
                                                <TableRow key={row.metric} hover>
                                                    <TableCell>{row.metric}</TableCell>
                                                    <TableCell align="right">{row.watchlistValue}</TableCell>
                                                    <TableCell align="right">{row.portfolioValue}</TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Paper>
                        )}

                        {!loading && !error && (
                            <Paper sx={{ ...compareSectionPaperSx, p: 1.5, display: 'grid', gap: 1.25 }}>
                                <Box sx={{ display: 'grid', gap: 0.4 }}>
                                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                        Actual portfolio risk impact
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        Shows how the actual portfolio&apos;s stock-specific risk changes when AI values are used where available.
                                    </Typography>
                                </Box>

                                {portfolioCompare?.available && Array.isArray(portfolioCompare.summary_rows) && portfolioCompare.summary_rows.length > 0 ? (
                                    <TableContainer component={Paper} sx={compareTableContainerSx}>
                                        <Table size="small" aria-label="AI vs standard portfolio risk impact table" sx={compareStripedTableSx}>
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell>Metric</TableCell>
                                                    <TableCell align="right">Standard</TableCell>
                                                    <TableCell align="right">AI</TableCell>
                                                    <TableCell align="right">Delta</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {portfolioCompare.summary_rows.map((row) => (
                                                    <TableRow key={row.label} hover>
                                                        <TableCell>{row.label}</TableCell>
                                                        <TableCell align="right">{formatMetricValue(row.standard, row.format)}</TableCell>
                                                        <TableCell align="right">{formatMetricValue(row.ai, row.format)}</TableCell>
                                                        <TableCell align="right" sx={getDeltaCellSx(row.delta)}>
                                                            {formatMetricDeltaValue(row.delta, row.format)}
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                ) : (
                                    <Alert severity="info" variant="outlined">
                                        {portfolioCompare?.message || 'Apply an actual portfolio to view this table.'}
                                    </Alert>
                                )}
                            </Paper>
                        )}

                        {!loading && !error && rows.length === 0 && (
                            <Alert severity="info">
                                No watchlist securities are currently available for comparison.
                            </Alert>
                        )}

                        {!loading && !error && rows.length > 0 && (
                            <Paper sx={{ ...compareSectionPaperSx, p: 1.5, display: 'grid', gap: 1 }}>
                                <Box sx={{ display: 'grid', gap: 0.35 }}>
                                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                        Security-level comparison
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        Every watchlist security in the compare universe. Gross weight and added-risk columns only populate for names that are in the current actual portfolio.
                                    </Typography>
                                </Box>
                                <TableContainer component={Paper} sx={{ ...compareTableContainerSx, maxHeight: 420 }}>
                                    <Table stickyHeader size="small" aria-label="AI vs standard security comparison table" sx={compareStripedTableSx}>
                                        <TableHead>
                                            <TableRow>
                                                {securityComparisonColumns.map((column) => (
                                                    <TableCell
                                                        key={column.id}
                                                        align={column.align}
                                                        sortDirection={securitySort.field === column.id ? securitySort.direction : false}
                                                    >
                                                        <TableSortLabel
                                                            active={securitySort.field === column.id}
                                                            direction={securitySort.field === column.id ? securitySort.direction : 'asc'}
                                                            onClick={() => handleSecuritySort(column.id)}
                                                        >
                                                            {column.id === 'used_ai_prediction' ? (
                                                                <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.15 }}>
                                                                    <span>{column.label}</span>
                                                                    <Tooltip
                                                                        title="Checkmark means the AI value was used for this security in the comparison. Empty means it fell back to the standard non-AI value."
                                                                        arrow
                                                                    >
                                                                        <IconButton
                                                                            size="small"
                                                                            aria-label="Explain AI used"
                                                                            sx={{ p: 0.15, color: 'text.secondary' }}
                                                                            onClick={(event) => event.stopPropagation()}
                                                                        >
                                                                            <InfoOutlinedIcon sx={{ fontSize: 14 }} />
                                                                        </IconButton>
                                                                    </Tooltip>
                                                                </Box>
                                                            ) : column.label}
                                                        </TableSortLabel>
                                                    </TableCell>
                                                ))}
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {sortedSecurityRows.map((row) => (
                                                <TableRow key={row.cognive_id || row.sedol} hover>
                                                    <TableCell>{row.ticker || '—'}</TableCell>
                                                    <TableCell sx={{ fontFamily: 'monospace' }}>{row.identifier || '—'}</TableCell>
                                                    <TableCell align="right">{formatRiskValue(row.standard_idio_vol)}</TableCell>
                                                    <TableCell align="right">{formatRiskValue(row.ai_idio_vol)}</TableCell>
                                                    <TableCell align="right" sx={getDeltaCellSx(row.difference)}>
                                                        {formatDifferenceValue(row.difference)}
                                                    </TableCell>
                                                    <TableCell align="right">{formatWeightPct(row.gross_weight_pct)}</TableCell>
                                                    <TableCell align="right">{formatMetricValue(row.standard_specific_contribution_bps, 'bps')}</TableCell>
                                                    <TableCell align="right">{formatMetricValue(row.ai_specific_contribution_bps, 'bps')}</TableCell>
                                                    <TableCell align="right" sx={getDeltaCellSx(row.delta_specific_contribution_bps)}>
                                                        {formatMetricDeltaValue(row.delta_specific_contribution_bps, 'bps')}
                                                    </TableCell>
                                                    <TableCell align="center">{renderBooleanCheck(row.used_ai_prediction)}</TableCell>
                                                    <TableCell align="center">{renderBooleanCheck(row.inPortfolio)}</TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Paper>
                        )}
                    </Box>
                </DialogContent>
                <DialogActions sx={{ px: 2, py: 1.5 }}>
                    <Button
                        onClick={() => setOpen(false)}
                        sx={{ textTransform: 'none', fontWeight: 700, ...secondaryActionButtonSx }}
                    >
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}

IdioVolCompareTool.propTypes = {
    factorModel: PropTypes.string,
    factorDataModel: PropTypes.string,
    factorSnapshotDate: PropTypes.string,
    idioVolSource: PropTypes.string,
};
